import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Award, Globe2, Users, Calendar, ArrowRight, Star, ShieldCheck, Truck, Wrench,
  Cog, Heart, Hammer, Flame, Factory,
} from "lucide-react";
import { api } from "@/api";
import { useSite } from "@/lib/site";
import HeroSlider from "@/components/HeroSlider";
import { ProductCard, CategoryCard } from "@/components/Cards";
import InquiryForm from "@/components/InquiryForm";
import SeoHead from "@/lib/SeoHead";

const ICONS = { Wrench, ShieldCheck, Globe2, Cog, Heart, Hammer, Flame, Factory, Calendar, Users, Award, Truck };

export default function Home() {
  const { settings, loading } = useSite();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || []));
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data || []));
    api.get("/industries").then((r) => setIndustries(r.data || []));
    api.get("/testimonials").then((r) => setTestimonials(r.data || []));
  }, []);

  const stats = settings?.stats || { years: "25+", clients: "500+", countries: "20+", certifications: "10+" };
  const home = settings?.home_page || {};

  const orgJsonLd = {
    "@context": "https://schema.org", "@type": "Organization",
    name: settings?.site_name || "Durga Auto Industries",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: settings?.logo_url || undefined,
    contactPoint: [{ "@type": "ContactPoint", telephone: settings?.contact_phone, contactType: "sales", areaServed: "Worldwide" }],
    address: { "@type": "PostalAddress", streetAddress: settings?.contact_address },
  };

  if (loading && !settings) {
    return (
      <>
        <SeoHead title="Durga Auto Industries" description="Industrial fasteners manufacturer." />
        <section className="relative min-h-[88vh] overflow-hidden bg-[#1C1C1C]" data-testid="home-loading">
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#1C1C1C] to-[#1C1C1C]/80" />
          <div className="relative container-x min-h-[88vh] flex items-center pt-24 pb-20">
            <div className="w-full max-w-3xl space-y-6 animate-pulse">
              <div className="h-4 w-64 bg-white/10" />
              <div className="h-16 w-full max-w-2xl bg-white/10" />
              <div className="h-6 w-full max-w-xl bg-white/10" />
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead
        title={settings?.default_seo_title}
        description={settings?.default_seo_description}
        keywords={settings?.default_seo_keywords}
        image={settings?.hero_slides?.[0]?.image}
        jsonLd={orgJsonLd}
      />
      <HeroSlider slides={settings?.hero_slides || []} stats={stats} />

      {home.specialty && home.specialty.enabled !== false && <HoseClampSpotlight data={home.specialty} />}

      {/* Trust strip */}
      <section className="bg-[#0A0A0A] border-y border-[#222] py-8" data-testid="trust-strip">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            [ShieldCheck, "ISO 9001 / IATF 16949"],
            [Wrench, "In-House Heat Treatment"],
            [Truck, "Global Logistics"],
            [Award, "PPAP Level 3 Capable"],
          ].map(([Icon, label], i) => (
            <div key={i} className="flex items-center gap-3 text-white/80">
              <Icon size={22} className="text-[#C1121F] flex-shrink-0" />
              <span className="font-heading font-medium text-sm">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="section-y bg-white" data-testid="categories-section">
        <div className="container-x">
          <SectionHeader
            overline="Our Catalog"
            title="Fasteners for every industrial need"
            subtitle="From standard bolts and nuts to custom-engineered fasteners — manufactured to ISO, DIN and ASTM standards."
            link={["/products", "View all products"]}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {categories.map((c) => <CategoryCard key={c.id} category={c} />)}
          </div>
        </div>
      </section>

      {/* Process — exclusive editable section */}
      {home.process?.enabled !== false && home.process?.items?.length > 0 && (
        <section className="section-y bg-[#1C1C1C] text-white relative overflow-hidden" data-testid="home-process">
          <div className="absolute -right-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#C1121F]/10 blur-3xl pointer-events-none" />
          <div className="container-x relative">
            <div className="max-w-3xl mb-16">
              <div className="overline mb-4">{home.process.overline}</div>
              <h2 className="h-section text-white">{home.process.title}</h2>
              <p className="mt-4 body-lg text-white/70 font-body">{home.process.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#333] border border-[#333]">
              {home.process.items.map((p, i) => (
                <div key={i} className="bg-[#1C1C1C] p-8 hover:bg-[#0A0A0A] transition-colors group" data-testid={`home-process-${i}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="font-heading font-bold text-5xl text-[#C1121F] tabular-nums">{p.num}</span>
                    <span className="w-8 h-px bg-[#C1121F]/40" />
                  </div>
                  <h3 className="mt-6 font-heading font-semibold text-xl text-white">{p.title}</h3>
                  <p className="mt-3 font-body text-sm text-white/60 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About snippet */}
      <section className="section-y bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="about-strip">
        <div className="absolute inset-0 opacity-20">
          <img src={settings?.hero_slides?.[1]?.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent" />
        </div>
        <div className="relative container-x grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline mb-5">About Durga</div>
            <h2 className="h-section text-white">A quarter-century of fastener engineering excellence.</h2>
            <p className="mt-6 body-lg text-white/70 font-body">
              Founded in 1998, Durga Auto Industries supplies precision fasteners to automotive OEMs, construction firms,
              oil & gas EPCs and equipment manufacturers across 20+ countries. Our 60,000 sq ft facility in Ludhiana
              integrates forging, heat treatment, plating and metallurgical QC under one roof.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              {[
                [Calendar, stats.years, "Years"],
                [Users, stats.clients, "Clients"],
                [Globe2, stats.countries, "Countries"],
                [Award, stats.certifications, "Certifications"],
              ].map(([Icon, val, label], i) => (
                <div key={i} className="border-l-2 border-[#C1121F] pl-4">
                  <div className="flex items-center gap-2"><Icon size={16} className="text-[#C1121F]" /><span className="font-heading text-3xl font-bold text-white">{val}</span></div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-white/50 font-body">{label}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-primary mt-10 group" data-testid="home-cta-about">
              Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] bg-[#1C1C1C] border border-[#333] overflow-hidden">
              <img src={settings?.hero_slides?.[1]?.image || settings?.hero_slides?.[0]?.image} alt="Durga manufacturing facility" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#C1121F] text-white p-6 max-w-xs">
              <div className="font-heading text-3xl font-bold">25+</div>
              <div className="font-body text-sm mt-1">Years of precision engineering — across India and 20+ export markets.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="section-y bg-[#F5F5F5]" data-testid="featured-products">
        <div className="container-x">
          <SectionHeader
            overline="Featured Products"
            title="Catalog highlights"
            subtitle="Top-spec fasteners trusted by Tier-1 OEMs and EPC contractors."
            link={["/products", "View full catalog"]}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Why Durga — exclusive editable section */}
      {home.why?.enabled !== false && home.why?.items?.length > 0 && (
        <section className="section-y bg-white" data-testid="home-why">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
              <div className="lg:col-span-6">
                <div className="overline mb-4">{home.why.overline}</div>
                <h2 className="h-section text-[#1C1C1C]">{home.why.title}</h2>
              </div>
              <p className="lg:col-span-6 body-lg text-[#4B5563] font-body">{home.why.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {home.why.items.map((w, i) => {
                const Icon = ICONS[w.icon] || Wrench;
                return (
                  <div key={i} className="bg-[#F5F5F5] p-8 border border-transparent hover:border-[#C1121F] hover:bg-white transition-all group" data-testid={`home-why-${i}`}>
                    <div className="w-14 h-14 bg-[#1C1C1C] text-[#C1121F] flex items-center justify-center group-hover:bg-[#C1121F] group-hover:text-white transition-colors">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-6 font-heading font-semibold text-xl text-[#1C1C1C]">{w.title}</h3>
                    <p className="mt-3 font-body text-sm text-[#4B5563] leading-relaxed">{w.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Industries */}
      <section className="section-y bg-[#F5F5F5]" data-testid="industries-section">
        <div className="container-x">
          <SectionHeader
            overline="Industries Served"
            title="Engineered for the world's hardest jobs"
            subtitle="From automotive chassis to refinery flanges — our fasteners hold what matters."
            link={["/industries", "All industries"]}
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12">
            {industries.map((ind) => (
              <Link key={ind.id} to={`/industries/${ind.slug}`} className="group card-light p-6 text-center" data-testid={`industry-${ind.slug}`}>
                <div className="w-12 h-12 mx-auto bg-[#1C1C1C] text-[#C1121F] flex items-center justify-center mb-4 group-hover:bg-[#C1121F] group-hover:text-white transition-colors">
                  <span className="font-heading font-bold text-lg">{ind.name?.charAt(0)}</span>
                </div>
                <div className="font-heading font-semibold text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{ind.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-y bg-[#1C1C1C] text-white" data-testid="testimonials-section">
          <div className="container-x">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="overline mb-4">What Clients Say</div>
              <h2 className="h-section text-white">Trusted by industrial leaders</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="card-dark p-8" data-testid={`testimonial-${t.id}`}>
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => (
                      <Star key={i} size={14} className="text-[#C1121F] fill-[#C1121F]" />
                    ))}
                  </div>
                  <p className="font-body text-white/80 leading-relaxed">"{t.content}"</p>
                  <div className="mt-6 pt-5 border-t border-[#333]">
                    <div className="font-heading font-semibold text-white">{t.name}</div>
                    <div className="font-body text-xs text-white/60 mt-1">{t.role}{t.company && ` · ${t.company}`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA — exclusive editable section */}
      {home.cta?.enabled !== false && (
        <section className="section-y bg-[#C1121F] text-white" data-testid="home-cta">
          <div className="container-x text-center max-w-3xl mx-auto">
            {home.cta?.overline && <div className="overline !text-white/70 mb-4">{home.cta.overline}</div>}
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white">{home.cta?.title}</h2>
            {home.cta?.subtitle && <p className="mt-6 body-lg text-white/80 font-body">{home.cta.subtitle}</p>}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to={home.cta?.cta_primary_link || "/contact"} className="bg-[#1C1C1C] text-white hover:bg-black px-7 py-3 font-heading font-semibold inline-flex items-center gap-2 group">
                {home.cta?.cta_primary_text || "Request a Quote"} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to={home.cta?.cta_secondary_link || "/products"} className="border-2 border-white/40 text-white hover:bg-white hover:text-[#C1121F] px-7 py-3 font-heading font-semibold inline-flex items-center gap-2 transition-colors">
                {home.cta?.cta_secondary_text || "Browse Products"}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Inquiry form */}
      <section className="section-y bg-[#F5F5F5]" data-testid="home-inquiry">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline mb-4">Get a Quotation</div>
            <h2 className="h-section text-[#1C1C1C]">Need a quote on a specific fastener or custom part?</h2>
            <p className="mt-6 body-lg text-[#4B5563] font-body">
              Share your requirement — quantity, grade, standard, drawing — and we'll respond with pricing,
              lead time and engineering review within one business day.
            </p>
            <ul className="mt-8 space-y-4">
              {[
                "Engineering team available for drawing review",
                "PPAP / Material certificates with every shipment",
                "Worldwide export with full documentation",
                "Bulk and small-MOQ programs supported",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[#1C1C1C]">
                  <span className="w-5 h-5 bg-[#C1121F] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <InquiryForm variant="light" type="quote" title="Request a Quote" subtitle="We'll respond within 1 business day." />
        </div>
      </section>
    </>
  );
}

function HoseClampSpotlight({ data = {} }) {
  const stats = data.stats || [];
  const chips = data.footer_chips || [];
  return (
    <section className="relative bg-[#0A0A0A] text-white overflow-hidden" data-testid="hose-clamp-spotlight">
      <div className="absolute top-0 left-0 w-1 h-32 bg-[#C1121F]" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-32 h-1 bg-[#C1121F]" aria-hidden="true" />
      <div className="absolute top-20 -left-32 w-96 h-96 rounded-full bg-[#C1121F]/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="container-x py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-5">
          <div className="overline mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C1121F]" />
            {data.overline || "Our Specialty"}
          </div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl leading-[1.05] text-white">
            {data.headline || "Hose Clamp Screws."}
            {data.headline_accent && <span className="block text-[#C1121F]">{data.headline_accent}</span>}
          </h2>
          <p className="mt-6 body-lg text-white/70 font-body max-w-md">{data.subtitle}</p>

          {stats.length > 0 && (
            <ul className="mt-8 space-y-3">
              {stats.map((s, i) => (
                <li key={i} className="flex items-baseline gap-4 font-body text-sm border-b border-[#222] pb-3">
                  <span className="font-heading font-bold text-2xl text-[#C1121F] tabular-nums w-24 flex-shrink-0">{s.k}</span>
                  <span className="text-white/70">{s.v}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={data.cta_primary_link || "/contact"} className="btn-primary group" data-testid="hose-clamp-cta-quote">
              {data.cta_primary_text || "Request Quote"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to={data.cta_secondary_link || "/products"} className="btn-outline-light" data-testid="hose-clamp-cta-catalog">
              {data.cta_secondary_text || "See full catalog"}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7 relative">
          <div className="relative bg-white border border-[#333] shadow-[0_30px_60px_-15px_rgba(193,18,31,0.25)]">
            {data.badge && (
              <div className="absolute -top-4 -left-4 bg-[#C1121F] text-white px-4 py-2 z-10">
                <div className="font-heading font-bold text-sm uppercase tracking-wider">{data.badge}</div>
              </div>
            )}
            {data.image && (
              <img src={data.image} alt={data.headline || "Hose clamp screws"} className="w-full h-auto block" loading="eager" />
            )}
            {(data.footer_label || chips.length > 0) && (
              <div className="bg-[#1C1C1C] text-white px-6 py-4 flex flex-wrap items-center justify-between gap-4 border-t border-[#333]">
                {data.footer_label && <div className="font-heading font-semibold text-sm">{data.footer_label}</div>}
                {chips.length > 0 && (
                  <div className="flex items-center gap-4 text-xs uppercase tracking-wider text-white/60 font-body">
                    {chips.map((chip, i) => (
                      <span key={i} className="flex items-center gap-2">
                        {chip}
                        {i < chips.length - 1 && <span className="w-1 h-1 bg-[#C1121F] rounded-full" />}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ overline, title, subtitle, link }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
      <div className="max-w-2xl">
        <div className="overline mb-4">{overline}</div>
        <h2 className="h-section text-[#1C1C1C]">{title}</h2>
        {subtitle && <p className="mt-4 body-lg text-[#4B5563] font-body">{subtitle}</p>}
      </div>
      {link && (
        <Link to={link[0]} className="font-heading font-semibold text-[#C1121F] inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="section-link">
          {link[1]} <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}
